/* reviewsIQ — GatherUp review reporting. Shows volume + average rating over a
   date range, a monthly trend, rating distribution, response rate, and the most
   recent reviews. Alerts when a client has had no new review within its
   configured stale threshold. A multi-location client sees every location
   merged, a strip of how each is doing, and can narrow to one; the stale
   alert names the location that went quiet. The by-source (Google/Facebook)
   split is a follow-up once the authenticated GatherUp API is wired. */
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Select, Stat } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { ServicePitch } from '@/components/ServicePitch';
import { getReviews, getReviewSummary, qk, type ReviewItem, type ReviewLocation, type ReviewsPayload, type ReviewSummary } from '@/data/client';

const RANGE_OPTS = [
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '1y', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

const int = (n: number) => Math.round(n).toLocaleString('en-US');
const pct = (n: number) => `${Math.round(n)}%`;

function fmtDate(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function minsAgo(iso?: string): string {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

function Stars({ value, size = 15 }: { value: number; size?: number }) {
  const full = Math.round(value);
  return (
    <span className="rv-stars" aria-label={`${value.toFixed(1)} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= full ? 'is-on' : 'is-off'}><Icon name="star" size={size} /></span>
      ))}
    </span>
  );
}

const SENTIMENT_LABEL: Record<string, string> = { positive: 'Positive consensus', mixed: 'Mixed consensus', negative: 'Needs attention' };
const WINDOW_LABEL = (days?: number) => {
  if (!days) return 'recent reviews';
  if (days >= 360) return 'the last 12 months';
  if (days >= 88) return 'the last 90 days';
  if (days >= 28) return 'the last 30 days';
  return `the last ${days} days`;
};

function AiSummary({ range, location }: { range: string; location: string }) {
  const qc = useQueryClient();
  const key = [...qk.reviews, 'summary', range, location];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => getReviewSummary(range, false, location), staleTime: 5 * 60_000 });
  const refresh = useMutation({
    mutationFn: () => getReviewSummary(range, true, location),
    onSuccess: (s) => qc.setQueryData(key, s),
  });

  const s = data as ReviewSummary | undefined;

  return (
    <Card padding="lg" className="rv-ai">
      <div className="rv-ai__head">
        <div className="rv-ai__title">
          <span className="rv-ai__mark"><Icon name="sparkles" size={16} /></span>
          <div>
            <h2>AI review summary</h2>
            <p className="dash-muted">
              Consensus from {WINDOW_LABEL(s?.windowDays)}
              {s?.reviewCount ? ` · ${s.reviewCount} review${s.reviewCount === 1 ? '' : 's'}` : ''}
              {s?.averageInWindow ? ` · ${s.averageInWindow.toFixed(1)}★ avg` : ''}
            </p>
          </div>
        </div>
        <div className="rv-ai__headright">
          {s?.sentiment && !s.empty && !s.error && (
            <span className={`rv-sentiment rv-sentiment--${s.sentiment}`}>{SENTIMENT_LABEL[s.sentiment]}</span>
          )}
          <Button variant="ghost" size="sm" iconLeft={<Icon name="refresh-cw" size={14} />} disabled={refresh.isPending || isLoading} onClick={() => refresh.mutate()}>
            {refresh.isPending ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p className="dash-muted">Reading the reviews…</p>
      ) : s?.error ? (
        <p className="dash-muted">Couldn't generate a summary right now. {s.error.includes('ANTHROPIC') ? 'The AI key isn’t configured on the Worker.' : 'Try Refresh in a moment.'}</p>
      ) : s?.empty ? (
        <p className="dash-muted">No reviews in {WINDOW_LABEL(s.windowDays)} to summarize yet.</p>
      ) : s ? (
        <>
          {s.consensus && <p className="rv-ai__consensus">{s.consensus}</p>}
          <div className="rv-ai__cols">
            <div>
              <h3 className="rv-ai__coltitle rv-ai__coltitle--pos"><Icon name="check" size={14} /> What customers love</h3>
              {s.positives && s.positives.length > 0
                ? <ul className="rv-ai__list">{s.positives.map((p, i) => <li key={i}>{p}</li>)}</ul>
                : <p className="dash-muted">—</p>}
            </div>
            <div>
              <h3 className="rv-ai__coltitle rv-ai__coltitle--neg"><Icon name="alert-circle" size={14} /> Where to improve</h3>
              {s.negatives && s.negatives.length > 0
                ? <ul className="rv-ai__list">{s.negatives.map((n, i) => <li key={i}>{n}</li>)}</ul>
                : <p className="dash-muted">No recurring complaints — nice.</p>}
            </div>
          </div>
          {s.generatedAt && <p className="rv-ai__stamp">Analysis from {minsAgo(s.generatedAt)} · updates daily · Refresh for the latest</p>}
        </>
      ) : null}
    </Card>
  );
}

function StaleBanner({ d }: { d: ReviewsPayload }) {
  return (
    <Card padding="lg" className="rv-alert">
      <span className="rv-alert__icon"><Icon name="alert-circle" size={20} /></span>
      <div>
        <strong>No new reviews{d.staleLocation ? ` for ${d.staleLocation}` : ''} in {d.staleDays} days</strong>
        <p>
          {d.staleLocation ? 'That location\u2019s' : 'The'} last review came in {d.lastReviewDate ? fmtDate(d.lastReviewDate) : 'a while ago'} — past this client's{' '}
          {d.staleThreshold}-day threshold. A review push or follow-up campaign{d.staleLocation ? ' there' : ''} is worth considering.
        </p>
      </div>
    </Card>
  );
}

/* One card per location, each a button that narrows the page to it. Shown
   only when there is more than one feed, so a single-location client's page
   is unchanged. */
function LocationStrip({ locations, selected, onSelect }: { locations: ReviewLocation[]; selected: string; onSelect: (id: string) => void }) {
  return (
    <div className="rv-locs" role="group" aria-label="Reviews by location">
      {locations.map((l) => {
        const on = selected === l.id;
        return (
          <button
            key={l.id} type="button"
            className={`rv-locs__item${on ? ' is-on' : ''}${l.isStale ? ' is-stale' : ''}`}
            onClick={() => onSelect(on ? '' : l.id)}
            aria-pressed={on}
          >
            <span className="rv-locs__name">{l.label}{l.primary && <Badge variant="neutral">Primary</Badge>}</span>
            {l.error
              ? <span className="rv-locs__sub is-error">Could not load this location&rsquo;s reviews</span>
              : <>
                  <span className="rv-locs__kpi">{l.average ? `${l.average.toFixed(1)}★` : '—'}<small>{int(l.total)} review{l.total === 1 ? '' : 's'}</small></span>
                  <span className={`rv-locs__sub${l.isStale ? ' is-stale' : ''}`}>
                    {l.lastReviewDate ? `Last review ${fmtDate(l.lastReviewDate)}` : 'No reviews yet'}{l.isStale ? ` · ${l.staleDays} days quiet` : ''}
                  </span>
                </>}
          </button>
        );
      })}
    </div>
  );
}

function TrendChart({ trend }: { trend: { label: string; count: number }[] }) {
  const max = Math.max(1, ...trend.map((t) => t.count));
  return (
    <div className="dash-chart">
      {trend.map((t, i) => (
        <div className="dash-chart__col" key={`${t.label}-${i}`}>
          <div className="dash-chart__bar" style={{ height: `${(t.count / max) * 100}%` }}>
            <span>{t.count}</span>
          </div>
          <em>{t.label}</em>
        </div>
      ))}
    </div>
  );
}

function RatingBars({ dist }: { dist: number[] }) {
  const total = Math.max(1, dist.reduce((s, n) => s + n, 0));
  return (
    <ul className="rv-dist">
      {[5, 4, 3, 2, 1].map((star) => {
        const count = dist[star - 1] ?? 0;
        return (
          <li key={star}>
            <span className="rv-dist__label">{star}★</span>
            <span className="rv-dist__track"><i style={{ width: `${(count / total) * 100}%` }} /></span>
            <span className="rv-dist__count">{int(count)}</span>
          </li>
        );
      })}
    </ul>
  );
}

function ReviewRow({ r }: { r: ReviewItem }) {
  return (
    <li className="rv-item">
      <div className="rv-item__head">
        <div>
          <strong>{r.author || 'Anonymous'}</strong>
          <Stars value={r.rating} size={13} />
        </div>
        <span className="rv-item__date">{r.location && <span className="rv-loc"><Icon name="map-pin" size={11} />{r.location}</span>} {fmtDate(r.date)}</span>
      </div>
      {r.content && <p className="rv-item__body">{r.content}</p>}
      {r.response && (
        <div className="rv-item__reply">
          <span className="rv-item__replyfrom"><Icon name="quote" size={12} /> Response from {r.response.from || 'the business'}</span>
          <p>{r.response.content}</p>
        </div>
      )}
    </li>
  );
}

export function ReviewsIQ() {
  const [params, setParams] = useSearchParams();
  const range = params.get('range') || '90d';
  const setRange = (v: string) => setParams((p) => { p.set('range', v); return p; }, { replace: true });
  const location = params.get('location') || '';
  const setLocation = (v: string) => setParams((p) => { if (v) p.set('location', v); else p.delete('location'); return p; }, { replace: true });

  const { data, isLoading } = useQuery({ queryKey: [...qk.reviews, range, location], queryFn: () => getReviews(range, location) });

  if (isLoading) {
    return <div className="dash-stack"><Card padding="lg"><p className="dash-muted">Loading reviews…</p></Card></div>;
  }

  const d = data as ReviewsPayload | undefined;

  if (!d || !d.configured) {
    return (
      <ServicePitch
        icon="star"
        service="reviewsIQ"
        headline="Turn happy customers into your best salespeople."
        benefits={[
          'Your rating, review volume and response rate in one view, with every new review as it lands.',
          'Review requests sent after each job, so the customers who loved the work actually say so.',
          'A daily read on what customers praise and what they complain about, written for you.',
          'Five-star quotes pulled into your case studies and social posts, with permission and attribution.',
        ]}
        roles={['Account Manager', 'Brand / Strategy Manager', 'Paid Ads Manager']}
        ask="about review management and how reviewsIQ can turn happy customers into new ones."
        adminHint={<>Admin: add this client&rsquo;s Triton Reviews id under the <strong>Reviews</strong> connection, or open the <a href="https://tritonreviews.com/" target="_blank" rel="noreferrer">review manager</a>.</>}
      />
    );
  }

  const avg = d.average ?? 0;
  const recent = d.recent ?? [];
  const locations = d.locations ?? [];
  const multi = locations.length > 1;
  const selected = multi && locations.some((l) => l.id === location) ? location : '';
  const selectedLabel = locations.find((l) => l.id === selected)?.label;

  return (
    <div className="dash-stack">
      {/* toolbar */}
      <div className="lead-toolbar">
        <div className="lead-toolbar__filters">
          <label className="lead-field"><Icon name="calendar" size={15} />
            <Select options={RANGE_OPTS} value={range} onChange={(e) => setRange(e.target.value)} aria-label="Date range" />
          </label>
          {multi && (
            <label className="lead-field"><Icon name="map-pin" size={15} />
              <Select
                options={[{ value: '', label: 'All locations' }, ...locations.map((l) => ({ value: l.id, label: l.label }))]}
                value={selected} onChange={(e) => setLocation(e.target.value)} aria-label="Location"
              />
            </label>
          )}
        </div>
        <div className="lead-toolbar__right">
          <span className={`tq-src ${d.error ? 'is-error' : d.cached ? 'is-stale' : ''}`}>
            <span className="tq-src__dot" />
            {d.error ? 'Reviews service unavailable — showing last cached' : `Synced ${minsAgo(d.fetchedAt)}`}
          </span>
          <a className="tr-btn tr-btn--ghost tr-btn--sm" href="https://tritonreviews.com/" target="_blank" rel="noreferrer">
            <Icon name="external" size={14} /><span>Review manager</span>
          </a>
        </div>
      </div>

      {d.isStale && <StaleBanner d={d} />}

      {multi && <LocationStrip locations={locations} selected={selected} onSelect={setLocation} />}

      {/* KPIs */}
      <div className="dash-kpis">
        <Card padding="lg"><Stat label="Total reviews" value={int(d.total ?? 0)} sub={selectedLabel ? `all time · ${selectedLabel}` : 'all time'} /></Card>
        <Card padding="lg">
          <Stat label="Average rating" value={avg ? avg.toFixed(1) : '—'} sub={selectedLabel ? `all time · ${selectedLabel}` : 'all time'} />
          {!!avg && <div className="rv-kpistars"><Stars value={avg} /></div>}
        </Card>
        <Card padding="lg"><Stat label="Reviews in range" value={int(d.inRange ?? 0)} sub={d.avgInRange ? `${d.avgInRange.toFixed(1)}★ avg` : undefined} /></Card>
        <Card padding="lg"><Stat label="Response rate" value={pct((d.responseRate ?? 0) * 100)} sub="reviews you replied to" /></Card>
      </div>

      <AiSummary range={range} location={selected} />

      <div className="tq-two">
        <Card padding="lg">
          <div className="dash-panelhead"><div><h2>Reviews over time</h2><p>New reviews per month</p></div></div>
          {d.trend && d.trend.some((t) => t.count > 0)
            ? <TrendChart trend={d.trend} />
            : <p className="dash-muted">No reviews in this window yet.</p>}
        </Card>
        <Card padding="lg">
          <div className="dash-panelhead"><div><h2>Rating breakdown</h2><p>Distribution across recent reviews</p></div></div>
          <RatingBars dist={d.ratingDist ?? [0, 0, 0, 0, 0]} />
        </Card>
      </div>

      {/* by-source placeholder — wired once the authenticated source feed is added */}
      <Card padding="lg" className="rv-soon">
        <div>
          <h2>Reviews by source</h2>
          <p className="dash-muted">Google, Facebook, and other platform breakdowns are coming soon.</p>
        </div>
        <Badge variant="neutral">Coming soon</Badge>
      </Card>

      <Card padding={false} className="dash-leads">
        <div className="dash-panelhead dash-panelhead--pad">
          <div><h2>Recent reviews</h2><p>Newest first{d.truncated ? ' · showing the most recent pages' : ''}</p></div>
          {recent.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<Icon name="download" size={14} />}
              onClick={() => {
                const rows = recent.map((r) => [fmtDate(r.date), r.author, r.rating, r.content.replace(/\s+/g, ' '), r.response ? 'yes' : 'no', ...(multi ? [r.location ?? ''] : [])]);
                const csv = [['Date', 'Author', 'Rating', 'Review', 'Responded', ...(multi ? ['Location'] : [])], ...rows]
                  .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
                const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                const a = document.createElement('a');
                a.href = url; a.download = 'reviews.csv'; a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export CSV
            </Button>
          )}
        </div>
        {recent.length === 0
          ? <div className="lead-none">No reviews found for this client yet.</div>
          : <ul className="rv-list">{recent.map((r) => <ReviewRow key={r.id} r={r} />)}</ul>}
      </Card>
    </div>
  );
}
